import { isEqual } from "es-toolkit";
import htmx from "htmx.org";
import { css, html, nothing } from "lit";
import { map } from "lit/directives/map.js";
import { z } from "zod";
import { Datetime, DatetimeString } from "../../schemas/datetime.js";
import { nilable } from "../../schemas/nilable";
import { type ParserType, parser } from "../../schemas/parser";
import { Tag, Tags } from "../../schemas/tag";
import { hostStyles } from "../../styles/host.styles";
import { layoutStyles } from "../../styles/layout.styles";
import { WmElement } from "../element";

type Details = ParserType<typeof Details>;
const Details = parser(
  nilable(
    z.strictObject({
      // Even though the ID is not used to display, it is used in the event
      // of a deletion request.
      id: z.string(),
      createdAt: DatetimeString.schema,
      description: z.string(),
      doneAt: z.optional(DatetimeString.schema),
      dueBy: z.optional(DatetimeString.schema),
      tags: Tags.schema,
    }),
  ),
);

export class WmTaskDetails extends WmElement {
  static readonly TAG_NAME = "wm-task-details";

  static override properties = {
    details: {
      attribute: false,
      schema: Details.schema,
      hasChanged: (current: Details, old: Details) => !isEqual(current, old),
    },
  };

  static styles = [
    hostStyles,
    layoutStyles,
    css`
      .tags {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        gap: var(--wa-space-2xs);
      }
    `,
  ];

  declare details: Details;

  constructor() {
    super();
    this.details = undefined;
  }

  render() {
    if (this.details == null) {
      return nothing;
    }

    // TODO: probably all the actions should be events? And the AJAX swap be decided higher level?
    const { description, createdAt } = this.details;
    return html`
      <div style="--min-column-size: 14ch; gap: var(--wa-space-2xs);">
        <div>
          <small style="color: var(--wa-color-text-quiet);">Description</small><br />
          <strong>${description}</strong>
        </div>
        <div>
          <small style="color: var(--wa-color-text-quiet);">Created At</small><br />
          ${Datetime.formatPretty(Datetime.codec.decode(createdAt))}
        </div>
        ${this.#renderDoneAt()}
        ${this.#renderDueBy()}
        ${this.#renderTags()}
        ${this.#renderActions()}
      </div>`;
  }

  #renderDoneAt() {
    const { doneAt } = this.details ?? {};

    if (doneAt == null) {
      return nothing;
    } else {
      return html`
        <div>
          <small style="color: var(--wa-color-text-quiet);">Done At</small><br />
          ${Datetime.formatPretty(Datetime.codec.decode(doneAt))}
        </div>
      `;
    }
  }

  #renderDueBy() {
    const { dueBy } = this.details ?? {};

    if (dueBy == null) {
      return nothing;
    } else {
      return html`
        <div>
          <small style="color: var(--wa-color-text-quiet);">Due By</small><br />
          ${Datetime.formatPretty(Datetime.codec.decode(dueBy))}
        </div>
      `;
    }
  }

  #renderTags() {
    const { tags = [] } = this.details ?? {};
    const renderEmpty = () => html`None`;
    const renderTags = (tags: Tags) => html`
      <div class="tags">
        ${map(tags, (t) => Tag.format(t, { size: "xs" }))}
      </div>
    `;

    return html`
      <div>
        <small style="color: var(--wa-color-text-quiet);">Tags</small><br />
        ${tags.length !== 0 ? renderTags(tags) : renderEmpty()}
      </div>
      `;
  }

  #renderActions() {
    const { id, doneAt } = this.details ?? {};
    if (id == null) {
      return nothing;
    }

    return html`
        <div>
          <small style="color: var(--wa-color-text-quiet);">Actions</small><br />
          <div>
            <wa-button size="xs" variant="success" @click=${() => htmx.ajax("GET", `/app/tasks/${id}`, { target: "body", push: "true" })}>Edit</wa-button>
            ${
              doneAt
                ? html`
                  <wa-button size="xs" variant="success" @click=${() => htmx.ajax("POST", `/app/tasks/${id}/undone`)}>Undone</wa-button>
                `
                : html`
                  <wa-button size="xs" variant="success" @click=${() => htmx.ajax("POST", `/app/tasks/${id}/done`)}>Done</wa-button>
                `
            }
            <wm-delete-button size="xs" @click=${(e: PointerEvent) => this.dispatchEvent(new WmDeletionRequest(e, id))}></wm-delete-button>
          </div>
        </div>`;
  }
}

customElements.define(WmTaskDetails.TAG_NAME, WmTaskDetails);

declare global {
  interface HTMLElementTagNameMap {
    [WmTaskDetails.TAG_NAME]: WmTaskDetails;
  }
}

export class WmDeletionRequest extends Event {
  static readonly EVENT_NAME = "wm-deletion-request";

  /**
  The original click event at the source of this one.
  */
  readonly source: PointerEvent;
  /**
  The ID of the object to delete.
  */
  readonly id: string;

  constructor(source: PointerEvent, id: string) {
    super(WmDeletionRequest.EVENT_NAME, {
      bubbles: true,
      cancelable: false,
      composed: true,
    });
    this.source = source;
    this.id = id;
  }
}
